/**
 * ConflictDetector (AJIP-16.02.006 · WS6)
 * ---------------------------------------------------------------------
 * Deterministic conflict detection.
 *
 * Founder Ruling A1:
 *   Conflict detection at Resolution AND independent revalidation at
 *   Activation. This is the permanent defence-in-depth pattern.
 *
 * A journey is BLOCKED (state) or otherwise unavailable is treated as
 * a conflict; a WAITING journey is a resumable state and is signalled
 * separately by the Restoration Resolver — the detector still reports
 * ACTIVE_INSTANCE_EXISTS for WAITING/IN_PROGRESS/STARTED so the caller
 * can decide the correct action.
 */
import { CONFLICT_STATUSES } from './ResolutionDecisionContract';
import { STATES, isTerminalState } from '../core/JourneyState';

const REENTRY_RULE_PREFIX = 'no-reentry-after:';

const _existingContext = (engine, journeyId) => {
  if (!engine || typeof engine.contextOf !== 'function') return null;
  return engine.contextOf(journeyId);
};

const detect = ({
  request,
  definition,
  engine,
  registrationService,
  inFlightRequestIds = null,
  idempotencyStore = null,
}) => {
  const trace = [];
  const flag = (kind, details = {}) => Object.freeze({
    hasConflict: true, kind, details: Object.freeze({ ...details }), trace: Object.freeze(trace.slice()),
  });
  const ok = () => Object.freeze({
    hasConflict: false, kind: CONFLICT_STATUSES.NONE, details: Object.freeze({}), trace: Object.freeze(trace.slice()),
  });

  // 1. Duplicate request (already resolved to ACTIVATED).
  if (idempotencyStore && typeof idempotencyStore.has === 'function'
      && request && typeof request.requestId === 'string'
      && idempotencyStore.has(request.requestId)) {
    trace.push({ rule: 'duplicate-request', ok: false });
    return flag(CONFLICT_STATUSES.DUPLICATE_REQUEST, { requestId: request.requestId });
  }
  trace.push({ rule: 'duplicate-request', ok: true });

  // 2. Simultaneous activation attempts (in-flight lock).
  if (inFlightRequestIds && inFlightRequestIds instanceof Set
      && request && inFlightRequestIds.has(request.requestId)) {
    trace.push({ rule: 'simultaneous', ok: false });
    return flag(CONFLICT_STATUSES.SIMULTANEOUS_ACTIVATION, { requestId: request.requestId });
  }
  trace.push({ rule: 'simultaneous', ok: true });

  // Prerequisites for the remaining checks.
  if (!definition) {
    trace.push({ rule: 'definition-present', ok: false });
    return ok();
  }
  trace.push({ rule: 'definition-present', ok: true });

  // 3. Ownership mismatch (registered principal ≠ request principal).
  const principalRef = (request && request.principalContext && request.principalContext.principalRef) || null;
  const ctxExisting = _existingContext(engine, definition.journeyId);
  if (ctxExisting && ctxExisting.flagStatus) {
    // The engine context does not itself store ownerRef today; the
    // engine attaches ownership via PersistenceService's principalRef.
    // For same-engine conflict, we treat the context's existence as
    // owned by the caller only when no other principal state is
    // available. Ownership is authoritatively enforced by the
    // Persistence Framework's OwnershipGuard on restoration.
  }
  trace.push({ rule: 'ownership', ok: true });

  // 4. Existing active instance (STARTED, IN_PROGRESS, WAITING).
  if (ctxExisting) {
    const s = ctxExisting.currentState;
    if (s === STATES.BLOCKED) {
      trace.push({ rule: 'blocked-instance', ok: false });
      return flag(CONFLICT_STATUSES.BLOCKED_JOURNEY_EXISTS, { state: s });
    }
    if (s === STATES.WAITING || s === STATES.IN_PROGRESS || s === STATES.STARTED) {
      trace.push({ rule: 'active-instance', ok: false, state: s });
      return flag(CONFLICT_STATUSES.ACTIVE_INSTANCE_EXISTS, { state: s });
    }
    if (isTerminalState(s)) {
      const reentry = (definition.eligibilityRules || []).find((r) => r === `${REENTRY_RULE_PREFIX}completed`);
      if (reentry && s === STATES.COMPLETED) {
        trace.push({ rule: 'reentry', ok: false });
        return flag(CONFLICT_STATUSES.REENTRY_RESTRICTED, { state: s });
      }
    }
  }
  trace.push({ rule: 'active-instance', ok: true });

  // 5. Incompatible journey active (conflicts-with:*).
  const conflictsWith = (definition.eligibilityRules || [])
    .filter((r) => typeof r === 'string' && r.startsWith('conflicts-with:'))
    .map((r) => r.slice('conflicts-with:'.length));
  for (const rivalId of conflictsWith) {
    const rivalCtx = _existingContext(engine, rivalId);
    if (rivalCtx && !isTerminalState(rivalCtx.currentState)) {
      trace.push({ rule: 'conflicts-with', ok: false, rivalId });
      return flag(CONFLICT_STATUSES.INCOMPATIBLE_JOURNEY_ACTIVE, { rivalJourneyId: rivalId });
    }
  }
  trace.push({ rule: 'conflicts-with', ok: true });

  // 6. Version conflict — request asked for a specific version and the
  //    registered version differs. VersionResolutionPolicy already
  //    handles this; the detector reports it defensively.
  if (request && Number.isInteger(request.journeyVersion)
      && request.journeyVersion !== definition.journeyVersion) {
    trace.push({ rule: 'version-conflict', ok: false });
    return flag(CONFLICT_STATUSES.VERSION_CONFLICT, {
      requestedVersion: request.journeyVersion,
      registeredVersion: definition.journeyVersion,
    });
  }
  trace.push({ rule: 'version-conflict', ok: true });

  // 7. Ownership check against registrationService audit — defensive.
  //    If a future package attaches an ownerRef to the registered
  //    definition, honour it. Today the field is not populated so
  //    this check is a no-op.
  if (definition.owner && principalRef && definition.registrationStatus === 'registered'
      && definition.owner !== 'Founder' /* default authoring identity */
      && definition.owner !== principalRef) {
    trace.push({ rule: 'ownership-boundary', ok: false });
    return flag(CONFLICT_STATUSES.OWNERSHIP_MISMATCH, {
      definitionOwner: definition.owner,
      principalRef,
    });
  }
  trace.push({ rule: 'ownership-boundary', ok: true });

  // registrationService is consulted only through published methods.
  if (registrationService && typeof registrationService.getRegistrationStatus === 'function') {
    const status = registrationService.getRegistrationStatus(definition.journeyId);
    if (!status) {
      trace.push({ rule: 'registration-status', ok: false });
      return flag(CONFLICT_STATUSES.VERSION_CONFLICT, { reason: 'REGISTRATION_MISSING' });
    }
  }
  trace.push({ rule: 'registration-status', ok: true });

  return ok();
};

export { detect, REENTRY_RULE_PREFIX };
